import { useState, useEffect, useCallback } from 'react';

export interface SectionProgress {
  id: string;
  title: string;
  icon: string;
  color: string;
  visited: boolean;
  visitedAt?: string;
  timeSpent: number; // seconds
}

const SECTIONS: Omit<SectionProgress, 'visited' | 'visitedAt' | 'timeSpent'>[] = [
  { id: 'rwa-entry',      title: 'RWA入门认知',       icon: 'ri-seedling-line',         color: '#34D399' },
  { id: 'blockchain',     title: '区块链技术基础',     icon: 'ri-links-line',            color: '#60A5FA' },
  { id: 'policy',         title: '全球政策法规库',     icon: 'ri-global-line',           color: '#A78BFA' },
  { id: 'cases',          title: 'RWA案例深度库',      icon: 'ri-folder-chart-line',     color: '#F59E0B' },
  { id: 'hk-institutions',title: '香港持牌机构图谱',   icon: 'ri-building-2-line',       color: '#FB923C' },
  { id: 'landing',        title: '企业落地SOP',        icon: 'ri-route-line',            color: '#F472B6' },
  { id: 'news',           title: '资讯与会议',         icon: 'ri-news-line',             color: '#34D399' },
  { id: 'glossary',       title: 'RWA名词百科',        icon: 'ri-book-2-line',           color: '#F59E0B' },
  { id: 'blockchain-tech',title: '区块链技术架构',     icon: 'ri-cpu-line',              color: '#7C3AED' },
  { id: 'platforms',      title: '平台资源导航',       icon: 'ri-compass-discover-line', color: '#7C3AED' },
  { id: 'future-vision',  title: 'RWA未来展望',        icon: 'ri-planet-line',           color: '#8B5CF6' },
  { id: 'about',          title: '关于我们',           icon: 'ri-information-line',      color: '#A78BFA' },
];

const STORAGE_KEY = 'rwa99_learning_progress';

export function useLearningProgress() {
  const [progress, setProgress] = useState<SectionProgress[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const saved: Record<string, { visited: boolean; visitedAt?: string; timeSpent: number }> = stored ? JSON.parse(stored) : {};
    setProgress(
      SECTIONS.map((s) => ({
        ...s,
        visited: saved[s.id]?.visited ?? false,
        visitedAt: saved[s.id]?.visitedAt,
        timeSpent: saved[s.id]?.timeSpent ?? 0,
      }))
    );
  }, []);

  const markVisited = useCallback((sectionId: string) => {
    setProgress((prev) => {
      const updated = prev.map((s) =>
        s.id === sectionId && !s.visited
          ? { ...s, visited: true, visitedAt: new Date().toISOString() }
          : s
      );
      const toSave: Record<string, { visited: boolean; visitedAt?: string; timeSpent: number }> = {};
      updated.forEach((s) => { toSave[s.id] = { visited: s.visited, visitedAt: s.visitedAt, timeSpent: s.timeSpent }; });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
      return updated;
    });
  }, []);

  const resetProgress = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setProgress(SECTIONS.map((s) => ({ ...s, visited: false, timeSpent: 0 })));
  }, []);

  const visitedCount = progress.filter((s) => s.visited).length;
  const totalCount = progress.length;
  const percentage = totalCount > 0 ? Math.round((visitedCount / totalCount) * 100) : 0;

  // Recommend next unvisited section
  const nextSection = progress.find((s) => !s.visited);

  // Learning path suggestion based on visited sections
  const getLearningPath = (): string[] => {
    if (visitedCount === 0) return ['从RWA入门认知开始，建立基础概念', '了解区块链技术基础', '查阅全球政策法规'];
    if (visitedCount < 3) return ['继续学习区块链技术基础', '查阅全球政策法规库', '浏览RWA案例深度库'];
    if (visitedCount < 6) return ['深入研究香港持牌机构图谱', '学习企业落地SOP流程', '查阅RWA名词百科'];
    if (visitedCount < 9) return ['探索平台资源导航', '关注资讯与会议动态', '了解RWA未来展望'];
    return ['恭喜！您已完成大部分学习内容', '可以回顾重点板块深化理解', '欢迎参与社区讨论分享心得'];
  };

  return { progress, markVisited, resetProgress, visitedCount, totalCount, percentage, nextSection, getLearningPath };
}
